import React, { useRef } from 'react';
import styled from 'styled-components';

import { useOutsideAlerter } from '../transcript/useClickOutside';

import type { MediaFormat } from '../common/FormatDropdown';

type DownloadOption = {
  label: string;
  description: string;
};

type Props = {
  onClick: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    format: MediaFormat,
  ) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

const downloadOptions: DownloadOption[] = [
  {
    label: 'PNG',
    description: 'Ideal for static graphics',
  },
  {
    label: 'MP4',
    description: 'Ideal for animated graphics and video use',
  },
  {
    label: 'GIF',
    description: 'Ideal for short, looping animations',
  },
];

const ClipDownloadDropdown = ({ onClick, isOpen, setIsOpen }: Props) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useOutsideAlerter(dropdownRef, () => setIsOpen(false));

  if (!isOpen) return null;

  return (
    <MenuContainer ref={dropdownRef}>
      <MenuItems>
        {downloadOptions.map(option => (
          <DropdownListItem
            key={option.label}
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
              onClick(e, option.label.toLowerCase() as MediaFormat)
            }
          >
            <Content>
              <Label>{option.label}</Label>
              <Description>{option.description}</Description>
            </Content>
          </DropdownListItem>
        ))}
      </MenuItems>
    </MenuContainer>
  );
};

export default ClipDownloadDropdown;

const MenuContainer = styled.div`
  position: absolute;
  z-index: 1000;
  top: calc(100% + 5px);
  right: 0;
  background: var(--background);
  box-shadow: var(--elevation-sm);
`;

const MenuItems = styled.div`
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--background);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 4px 0;
  overflow: hidden;
`;

const DropdownListItem = styled.button`
  display: flex;
  align-items: center;
  padding: 1px 6px;
  width: 270px;
  cursor: pointer;
  background: none;
  border: none;
  outline: none;
  cursor: pointer;

  &:hover {
    background: color-mix(in srgb, var(--border) 30%, transparent);
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  flex: 1;
  padding: 9px 10px;
  border-radius: 6px;
  overflow: hidden;
`;

const Label = styled.p`
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 20px;
  color: var(--text-secondary);
  margin: 0;
  white-space: pre-wrap;
`;

const Description = styled.p`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  color: var(--text-muted);
  margin: 0;
`;
